import useTitle from "@/hooks/useTitle";
import { useNavigate } from "react-router";
import { useGetUserGrowthSparkRequestsQuery } from '@/features/userGrowthSparkRequests/userGrowthSparkRequestsApiSlice';
import moment from "moment";
import { UserIcon, AtSymbolIcon, ClockIcon, BuildingOfficeIcon } from '@heroicons/react/24/solid';
import { SparklesIcon } from '@heroicons/react/24/outline';
import useAuth from '@/hooks/useAuth';
import EtLogo from '@/assets/images/EtLogo2.png';
import EtLogoutIcon1SVGComp from "@/assets/img/icons/svgComp/EtLogoutIcon1SVGComp";
import { useSendLogoutMutation } from "@/features/auth/authApiSlice";

const EtUserGrowthSparkRequests = () => {

  // First Created - 15 Oct 2025
  // Author - Atul Pandey
  // Purpose - For Et Web Admins and Client Excellence Team to view and act on new prospect requests from eternity & trinity website

  // 0 - Set Page Title & Navigaton

  useTitle('Et - Growth Spark Requests');
  const navigate = useNavigate();
  
  // 1 - 
  const { username, isAdmin } = useAuth();

  // 2 - Data - RTK Query & Mutations
  const { data: growthSparkData, isLoading, error, refetch } = useGetUserGrowthSparkRequestsQuery();
  const [sendLogout] = useSendLogoutMutation();

  const extractEntities = (adapterData) =>
    adapterData?.ids?.map(id => adapterData.entities[id]) || [];

  const sparkList = extractEntities(growthSparkData);

  // 3 - Handle Logout
  const handleLogout = async () => {
    try {
      await sendLogout().unwrap();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <div className="m-2 p-2 bg-gray-200 rounded font-poppins">
      {/* Header */}
      <div className="flex items-center gap-3 p-4">
        <img src={EtLogo} alt="Eternity" className="h-6 w-auto" />
        <h1 className="text-xl sm:text-3xl font-bold">Growth Spark Requests</h1>
        <div className="bg-orange-50 border border-orange-600 text-orange-800 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
          {sparkList.length} total
        </div>
        <div onClick={handleLogout} className="ml-auto relative group cursor-pointer">
          <div className="flex justify-center items-center rounded-full w-6 h-6 bg-gradient-to-r from-orange-800 to-orange-800 via-orange-600">
            <EtLogoutIcon1SVGComp className="h-3 w-3 text-white" />
          </div>
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
            Logout
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 px-2 sm:px-4">
        {sparkList
          .slice()
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((item, idx) => {
            console.log('trinity growth spark', item.aiGeneratedResponse)
            return(
            <div key={item._id+idx} className="bg-white rounded shadow-md p-4 space-y-2">
              <div className="flex items-center gap-2 border-b pb-1">
                <UserIcon className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-sm font-bold text-gray-800">{item.name}</p>
                  <p className="text-xs italic text-gray-500">{item.role}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-600">
                <BuildingOfficeIcon className="h-4 w-4 text-orange-600" />
                {item.organizationName}
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-600">
                <AtSymbolIcon className="h-4 w-4 text-orange-600" />
                {item.email}
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-600">
                <ClockIcon className="h-4 w-4 text-lime-600" />
                {moment(item.createdAt).format("DD MMM YYYY, hh:mm A")}
              </div>

              <div className="mt-2 border-t pt-2 text-xs text-gray-700">
                <p className="font-semibold text-orange-600">Growth Prompt:</p>
                <p className="text-gray-600">{item.growthPrompt}</p>
              </div>

              {item.aiGeneratedResponse && (
                <div className="mt-2 border-t pt-2 text-xs text-gray-700">
                  <p className="flex items-center gap-1 font-semibold text-orange-600">
                    <SparklesIcon className="h-4 w-4" />
                    Trinity Growth Spark
                  </p>
                  <div
                    className="text-gray-700 leading-snug mt-1 text-xs"
                    dangerouslySetInnerHTML={{ __html: item.aiGeneratedResponse }}
                  />
                </div>
              )}
            </div>
          )})}
      </div>
    </div>
  );
};

export default EtUserGrowthSparkRequests;